import React, { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Upload, Image, X, Wand2, AlertCircle } from 'lucide-react';
import { Button } from '../../../shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../shared/components/ui/card';
import { PersonalizationOptions } from './PersonalizationOptions';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export const UploadForm = ({ onGenerate, isLoading, onReset, hasResult }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const [personalization, setPersonalization] = useState({
    expression: 'confident_smile',
    presence: 'approachable',
    attire: 'smart_casual',
    professional_context: 'startup_tech',
    background: 'office_blurred',
    lighting: 'soft_natural',
    finish: 'natural',
    pose: 'slight_lean',
    framing: 'head_shoulders'
  });
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'Please upload a JPG, PNG, or WebP image.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size must be less than 10MB.';
    }
    return null;
  };

  const handleFile = useCallback((file) => {
    setError(null);
    
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSelectedFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, [handleFile]);

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreview(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onReset();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedFile) {
      onGenerate(selectedFile, personalization);
    }
  };

  // Hide entire upload form after result is generated
  if (hasResult) {
    return null;
  }

  return (
    <section id="generator" className="py-12 md:py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Card className="shadow-xl border-border/50">
            <CardHeader className="text-center pb-2">
              <CardTitle className="font-display text-2xl md:text-3xl text-foreground">
                Upload Your Photo
              </CardTitle>
              <CardDescription className="text-base">
                Upload a clear photo of yourself to generate a professional LinkedIn profile picture
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Upload Area */}
                <div
                  className={`relative border-2 border-dashed rounded-2xl transition-all duration-200 ${
                    dragActive
                      ? 'border-primary bg-primary/5'
                      : preview
                      ? 'border-primary/50 bg-primary/5'
                      : 'border-border hover:border-primary/50 hover:bg-muted/50'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleInputChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    disabled={isLoading}
                  />
                  
                  {preview ? (
                    <div className="p-6">
                      <div className="relative max-w-xs mx-auto">
                        <img
                          src={preview}
                          alt="Preview"
                          className="w-full aspect-square object-cover rounded-xl shadow-lg"
                        />
                        {!isLoading && !hasResult && (
                          <button
                            type="button"
                            onClick={handleRemoveFile}
                            className="absolute -top-2 -right-2 w-8 h-8 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center shadow-md hover:bg-destructive/90 transition-colors z-20"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <p className="text-center text-sm text-muted-foreground mt-4">
                        {selectedFile?.name}
                      </p>
                    </div>
                  ) : (
                    <div className="p-12 text-center">
                      <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                        <Upload className="w-8 h-8 text-primary" />
                      </div>
                      <p className="text-lg font-medium text-foreground mb-2">
                        Drag & drop your photo here
                      </p>
                      <p className="text-sm text-muted-foreground mb-4">
                        or click to browse
                      </p>
                      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                        <Image className="w-4 h-4" />
                        <span>JPG, PNG, or WebP • Max 10MB</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Error Message */}
                {error && (
                  <div className="flex items-center gap-2 p-4 rounded-xl bg-destructive/10 text-destructive">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p className="text-sm font-medium">{error}</p>
                  </div>
                )}

                {/* Personalization Options */}
                {selectedFile && (
                  <PersonalizationOptions
                    personalization={personalization}
                    onPersonalizationChange={setPersonalization}
                    isLoading={isLoading}
                  />
                )}

                {/* Tips */}
                <div className="bg-muted/50 rounded-xl p-4">
                  <p className="text-sm font-medium text-foreground mb-2">For best results:</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Use a photo with good lighting and your face clearly visible</li>
                    <li>• Front-facing photos work best</li>
                    <li>• Avoid group photos or images with multiple people</li>
                    <li>• Higher resolution photos produce better results</li>
                  </ul>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button
                    type="submit"
                    variant="primary"
                    size="xl"
                    className="w-full"
                    disabled={!selectedFile || isLoading}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2 whitespace-nowrap text-sm sm:text-base">
                        <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin flex-shrink-0" />
                        <span className="hidden sm:inline">Optimising your professional presence…</span>
                        <span className="sm:hidden">Optimising…</span>
                      </span>
                    ) : (
                      <>
                        <Wand2 className="w-5 h-5" />
                        Generate Professional Photo
                      </>
                    )}
                  </Button>
                  {!selectedFile && (
                    <p className="text-sm text-muted-foreground text-center mt-3">
                      Please upload a photo to generate your professional profile picture
                    </p>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};
